import React from "react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";

import { IMAGES } from "@/assets/images";
import SVG from "@/assets/svg";
import CustomButton from "@/component/button";
import { CheckBoxInput, LabeledInput } from "@/component/input";
import { getPreferredUsersAsync } from "@/redux/actions/preferredTerms/PreferredTerms";
import { useAppDispatch } from "@/redux/hooks";
import { setUser } from "@/redux/reducers/user";
import { login } from "@/services/auth";
import { loginSupport } from "@/services/support-access";
import { ACCESS_LEVEL } from "@/utils/constants";
import logger from "@/runtime-utils/observability/logger";
import { getDeviceInfo } from "@/runtime-utils/client/device-info";

const SUPPORT_AUTH_ERROR_MESSAGES = new Set([
  "Admin not found",
  "Invalid password",
  "Username or email is required",
  "Invalid username or email format",
]);

const loginBodySx = {
  background: `url(${IMAGES.loginBg.src}) center / cover`,
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  alignItems: "center",
};

const loginBoxSx = {
  width: "472px",
  maxWidth: "100%",
  margin: "0 auto",
  background: "#fff",
  boxShadow: "0px 0px 7px #ccc",
  padding: "12px",
  borderRadius: "10px",
  fontFamily: "Poppins",
};

const getLoginErrorMessage = (message: any, supportMessage: any): string => {
  if (
    message === "Invalid Username or Password" &&
    supportMessage === "Admin not found"
  ) {
    return "Invalid Username or Password";
  }

  // If support message is more specific than a generic customer login failure,
  // or if customer login failure is generic, prioritize supportMessage.
  const result =
    supportMessage &&
    supportMessage !== "Admin not found" &&
    supportMessage !== "Invalid password"
      ? supportMessage
      : message ||
        supportMessage ||
        "An unexpected error occurred. Please try again.";
  return typeof result === "string" ? result : JSON.stringify(result);
};

function SignIn() {
  const [userNameOrEmail, setUserNameOrEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [shouldRemember, setShouldRemember] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  const router = useRouter();
  const dispatch = useAppDispatch();
  const sessionExpired =
    router.isReady && router.query.reason === "session-expired";

  const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    if (!userNameOrEmail || !password) {
      return setError("Please provide username or email and password");
    }
    setLoading(true);
    const lowerCaseEmail = userNameOrEmail.toLowerCase();

    const deviceInfo = getDeviceInfo();

    const response = await login({
      userNameOrEmail: lowerCaseEmail,
      password,
      shouldRemember,
      deviceInfo,
    });
    if (response.success) {
      dispatch(
        setUser({
          user: response.data,
          token: response?.token,
        })
      );
      const id = response.data?._id;
      // Attach user identity to Faro so all traces/errors are tagged with the
      // logged-in user — enables "what was this user doing when the error occurred?"
      import("@/runtime-utils/observability/faro").then(({ setFaroUser }) => {
        const tenantId = response.data?.tenantId ?? "";
        const role = response.data?.jobRoles?.[0] ?? response.data?.role;
        setFaroUser(id, tenantId, role);
      });
      dispatch(getPreferredUsersAsync(id));
      if (response.data?.approved) {
        const redirect = router.query.redirect;
        const redirectPath = Array.isArray(redirect) ? redirect[0] : redirect;
        router.push(redirectPath ?? "/dashboard");
      } else {
        router.push("/success");
      }
    } else {
      const supportResponse = await loginSupport({
        userNameOrEmail: lowerCaseEmail,
        password,
        shouldRemember,
        deviceInfo,
      });

      if (supportResponse.success) {
        dispatch(
          setUser({
            user: {
              ...supportResponse.data,
              accessLevel: ACCESS_LEVEL.ADMINISTRATOR,
              approved: true,
              isSupportUser: true,
              role: "support",
            },
            token: supportResponse?.token,
          })
        );
        router.push("/support/tenants");
        setLoading(false);
        return;
      }

      // errorToast(response.message);
      logger.debug("response.message", {
        customerLogin: response,
        supportLogin: supportResponse,
      });
      setError(getLoginErrorMessage(response.message, supportResponse.message));
    }
    setLoading(false);
  };

  // useEffect(() => {
  //   if (router.query.reason === "session-expired") {
  //     errorToast("To keep your account secure, we need you to sign in again. Please log in to continue.", {
  //       autoClose: 5000,
  //     });
  //     router.replace(
  //       "/sign-in",
  //       {
  //         query: {
  //           redirect: router.query.redirect || "/dashboard",
  //         },
  //       },
  //       { shallow: true }
  //     );
  //   }
  // }, [router.query.reason]);

  return (
    <>
      <Head>
        <title>Wisterias | Login</title>
      </Head>
      <main>
        <Snackbar
          open={sessionExpired}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={null}
        >
          <Alert
            severity="warning"
            variant="filled"
            sx={{ fontFamily: "Poppins" }}
          >
            Your session has timed out. Please log in again.
          </Alert>
        </Snackbar>
        <Box sx={loginBodySx}>
          <Box component="section" sx={loginBoxSx}>
            <Typography variant="h2" sx={{ textAlign: "center" }}>
              <SVG.Logo width="170px" />
            </Typography>
            <Typography
              variant="h3"
              sx={{
                textAlign: "center",
                padding: "12px",
                fontSize: "24px",
                fontWeight: "600",
              }}
            >
              LOG
              <Box
                component={"span"}
                sx={{
                  color: "#651F70",
                  fontSize: "24px",
                  fontWeight: "600",
                }}
              >
                IN
              </Box>
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert
                  severity="error"
                  color="error"
                  onClose={() => setError("")}
                  sx={{
                    lineHeight: "21px",
                  }}
                >
                  {error}
                </Alert>
              )}
              <FormGroup>
                <LabeledInput
                  label="User name or Email address"
                  type="email"
                  // testId="usernameOrEmail"
                  autoComplete="username"
                  placeholder="User name or Email address"
                  value={userNameOrEmail}
                  onChange={(e) => {
                    setUserNameOrEmail(e.target.value);
                    setError("");
                  }}
                />
              </FormGroup>
              <FormGroup className="userPassword">
                <LabeledInput
                  label="Password"
                  placeholder="Password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError("");
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "472px",
                    margin: "10px auto",
                  }}
                >
                  <CheckBoxInput
                    label="Remember me"
                    value={shouldRemember}
                    onChange={(e) => setShouldRemember(e.target.checked)}
                    sx={{
                      border: "0",
                      fontWeight: "400",
                      fontSize: "14px",
                      fontFamily: "Poppins",
                    }}
                  />
                  <Link href="/auth/forgot-password">
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#651F70",
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "Poppins",
                      }}
                    >
                      Forgot Password?
                    </Typography>
                  </Link>
                </Box>
              </FormGroup>

              <CustomButton
                type="submit"
                variant="contained"
                sx={{ width: "100% !important", margin: "auto !important" }}
                loading={loading}
                disabled={loading}
              >
                LOGIN
              </CustomButton>
            </Box>
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "300",
              }}
            >
              {" "}
              Don’t have an account?
              <Link href="/sign-up">
                <Box
                  component={"span"}
                  sx={{
                    color: "#651F70",
                    fontSize: "12px",
                    fontWeight: "600",
                    margin: "0px 9px",
                  }}
                >
                  Sign Up
                </Box>
              </Link>
            </Typography>
          </Box>
        </Box>
      </main>
    </>
  );
}

SignIn.guest = true;

export default SignIn;
